import type React from "react";
import { Typography, Box } from "@mui/material";
import { parseFormattedText } from "../utils";
import { FormattedTextPart } from "../types";

interface FormattedTextProps {
  text: string;
  variant?: "body1" | "body2" | "caption";
  color?: string;
  sx?: any;
}

export const FormattedText: React.FC<FormattedTextProps> = ({
  text,
  variant = "body2",
  color,
  sx = {},
}) => {
  const parts = parseFormattedText(text);

  const renderPart = (part: FormattedTextPart, index: number) => {
    // Handle line breaks
    if (part.text === "\n") {
      return <br key={index} />;
    }

    const baseProps = {
      component: "span" as const,
      variant,
      color,
    };

    switch (part.type) {
      case "heading1":
        return (
          <Typography
            key={index}
            variant="h4"
            component="div"
            color={color}
            sx={{
              fontWeight: "bold",
              fontSize: "1.5rem",
              lineHeight: 1.3,
              mb: 1,
              mt: index > 0 ? 1 : 0,
            }}
          >
            {part.text}
          </Typography>
        );

      case "heading2":
        return (
          <Typography
            key={index}
            variant="h5"
            component="div"
            color={color}
            sx={{
              fontWeight: "bold",
              fontSize: "1.3rem",
              lineHeight: 1.3,
              mb: 0.8,
              mt: index > 0 ? 0.8 : 0,
            }}
          >
            {part.text}
          </Typography>
        );

      case "heading3":
        return (
          <Typography
            key={index}
            variant="h6"
            component="div"
            color={color}
            sx={{
              fontWeight: "bold",
              fontSize: "1.1rem",
              lineHeight: 1.3,
              mb: 0.6,
              mt: index > 0 ? 0.6 : 0,
            }}
          >
            {part.text}
          </Typography>
        );

      case "bold":
        return (
          <Typography
            key={index}
            {...baseProps}
            sx={{
              fontWeight: "bold",
              display: "inline",
            }}
          >
            {part.text}
          </Typography>
        );

      case "italic":
        return (
          <Typography
            key={index}
            {...baseProps}
            sx={{
              fontStyle: "italic",
              display: "inline",
            }}
          >
            {part.text}
          </Typography>
        );

      case "strikethrough":
        return (
          <Typography
            key={index}
            {...baseProps}
            sx={{
              textDecoration: "line-through",
              display: "inline",
            }}
          >
            {part.text}
          </Typography>
        );

      case "code":
        return (
          <Box
            key={index}
            component="div"
            sx={{
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              padding: "8px 12px",
              borderRadius: "6px",
              fontFamily: "monospace",
              fontSize: "0.9em",
              margin: "4px 0",
              whiteSpace: "pre-wrap",
              border: "1px solid rgba(0, 0, 0, 0.1)",
            }}
          >
            {part.text}
          </Box>
        );

      case "monospace":
        return (
          <Typography
            key={index}
            {...baseProps}
            sx={{
              fontFamily: "monospace",
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              padding: "2px 4px",
              borderRadius: "3px",
              fontSize: "0.9em",
              display: "inline",
            }}
          >
            {part.text}
          </Typography>
        );

      default:
        return (
          <Typography key={index} {...baseProps} sx={{ display: "inline" }}>
            {part.text}
          </Typography>
        );
    }
  };

  return (
    <Box sx={{ ...sx, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
      {parts.map(renderPart)}
    </Box>
  );
};
